import type { ScooterAccessState } from '../services/api/types';

export type ScooterChipState =
  | 'checking'
  | 'ready'
  | 'needs_packs'
  | 'restricted'
  | 'unavailable';

export type PanelTone = 'ready' | 'warning' | 'danger' | 'restricted' | 'loading';

interface RideReadinessPresentationInput {
  accessState?: ScooterAccessState | null;
  readinessReady?: boolean | null;
  loading: boolean;
  error?: string | null;
  unresolvedCount: number;
}

interface RideReadinessPresentation {
  isRideAllowed: boolean;
  tone: PanelTone;
  iconColor: string;
  statusLabel: string;
  tagColor: 'primary' | 'success' | 'warning' | 'danger';
  title: string;
  summary: string;
}

const knownAccessStates = new Set<ScooterAccessState>([
  'active',
  'grace',
  'system_blocked',
  'admin_blocked',
]);

const isKnownAccessState = (value: string | null | undefined): value is ScooterAccessState =>
  !!value && knownAccessStates.has(value as ScooterAccessState);

export function chipFromAccessState(
  state?: string | null,
  readinessReady?: boolean | null,
): ScooterChipState | null {
  if (!isKnownAccessState(state)) return null;
  switch (state) {
    case 'admin_blocked':
      return 'restricted';
    case 'system_blocked':
    case 'grace':
      return 'needs_packs';
    case 'active':
      return readinessReady == null ? null : readinessReady ? 'ready' : 'needs_packs';
    default:
      return null;
  }
}

export const resolveRideReadinessPresentation = ({
  accessState,
  readinessReady,
  loading,
  error,
  unresolvedCount,
}: RideReadinessPresentationInput): RideReadinessPresentation => {
  const fallbackReady = !loading && !error && unresolvedCount === 0;
  const requiredPacksReady = readinessReady ?? fallbackReady;
  const fallbackTitle = unresolvedCount > 1 ? 'Packs needed to ride' : 'Pack needed to ride';
  const fallbackSummary = unresolvedCount > 1
    ? 'Add the required packs before riding.'
    : 'Add the required pack before riding.';

  if (loading) {
    return {
      isRideAllowed: false,
      tone: 'loading',
      iconColor: '#1677ff',
      statusLabel: 'Checking',
      tagColor: 'primary',
      title: 'Checking ride readiness',
      summary: 'Checking required packs for the selected scooter.',
    };
  }

  if (error) {
    return {
      isRideAllowed: false,
      tone: 'danger',
      iconColor: '#dc2626',
      statusLabel: 'Needs attention',
      tagColor: 'danger',
      title: fallbackTitle,
      summary: error,
    };
  }

  if (isKnownAccessState(accessState)) {
    if (accessState === 'admin_blocked') {
      return {
        isRideAllowed: false,
        tone: 'restricted',
        iconColor: '#7c3aed',
        statusLabel: 'Restricted',
        tagColor: 'primary',
        title: 'Restricted',
        summary: 'This scooter is on an admin hold. Contact support before riding.',
      };
    }

    if (accessState === 'system_blocked') {
      return {
        isRideAllowed: false,
        tone: 'danger',
        iconColor: '#dc2626',
        statusLabel: 'System Blocked',
        tagColor: 'danger',
        title: fallbackTitle,
        summary: fallbackSummary,
      };
    }

    if (accessState === 'grace') {
      return {
        isRideAllowed: true,
        tone: 'warning',
        iconColor: '#d97706',
        statusLabel: 'Grace',
        tagColor: 'warning',
        title: 'Ready to ride',
        summary: unresolvedCount > 0
          ? 'One more swap is allowed. Add the required pack before the next swap to keep riding.'
          : 'This scooter is in grace. One more swap is allowed before coverage must be restored.',
      };
    }

    return {
      isRideAllowed: requiredPacksReady,
      tone: requiredPacksReady ? 'ready' : 'warning',
      iconColor: requiredPacksReady ? '#16a34a' : '#d97706',
      statusLabel: requiredPacksReady ? 'Ready' : 'Needs Pack(s)',
      tagColor: requiredPacksReady ? 'success' : 'warning',
      title: requiredPacksReady ? 'Ready to ride' : fallbackTitle,
      summary: requiredPacksReady ? 'All required packs are active for this scooter.' : fallbackSummary,
    };
  }

  if (fallbackReady) {
    return {
      isRideAllowed: true,
      tone: 'ready',
      iconColor: '#16a34a',
      statusLabel: 'Ready',
      tagColor: 'success',
      title: 'Ready to ride',
      summary: 'All required packs are active for this scooter.',
    };
  }

  return {
    isRideAllowed: false,
    tone: 'danger',
    iconColor: '#dc2626',
    statusLabel: 'Needs attention',
    tagColor: 'danger',
    title: fallbackTitle,
    summary: fallbackSummary,
  };
};
